import * as THREE from 'three';
import {
  DEFAULT_WIND_LAYER_BLEND_MODE,
  DEFAULT_VELOCITY_TEXTURE_LEVEL,
  WIND_LIMITS,
  WIND_MAGNITUDE_RANGE,
  clamp,
  decodeWindVector,
  getWindLayerBlendLevels,
  getPressureTexturePath,
  getVelocityTexturePath,
  normaliseWindMagnitudeRange,
  resolveVelocityTextureLevel,
  withBasePath,
} from '../../constants.js';

const textureLoader = new THREE.TextureLoader();
const LEGACY_WIND_TEXTURE_PATH = withBasePath('/textures/flipped.png');
const TEMPERATURE_TEXTURE_PATH = withBasePath('/textures/STemp.png');
const windMagnitudeRangeCache = new WeakMap();

export function loadTextureAsync(url) {
  return new Promise((resolve, reject) => {
    textureLoader.load(url, resolve, undefined, reject);
  });
}

export async function createVelocityTexture(textureLevel = DEFAULT_VELOCITY_TEXTURE_LEVEL) {
  const resolvedLevel = resolveVelocityTextureLevel(textureLevel);
  let velocityTexture;
  let isFallback = false;

  try {
    velocityTexture = await loadTextureAsync(getVelocityTexturePath(resolvedLevel));
  } catch (error) {
    velocityTexture = await loadTextureAsync(LEGACY_WIND_TEXTURE_PATH);
    isFallback = true;
  }

  velocityTexture.userData.level = resolvedLevel;
  velocityTexture.userData.isFallback = isFallback;
  velocityTexture.minFilter = THREE.NearestFilter;
  velocityTexture.magFilter = THREE.NearestFilter;
  velocityTexture.generateMipmaps = false;
  velocityTexture.wrapS = THREE.RepeatWrapping;
  velocityTexture.wrapT = THREE.ClampToEdgeWrapping;
  velocityTexture.colorSpace = THREE.NoColorSpace;
  velocityTexture.needsUpdate = true;

  return velocityTexture;
}

export async function createVelocityLayerTexture(
  textureLevel = DEFAULT_VELOCITY_TEXTURE_LEVEL,
  layerBlendMode = DEFAULT_WIND_LAYER_BLEND_MODE
) {
  const resolvedLevel = resolveVelocityTextureLevel(textureLevel);
  const layerLevels = getWindLayerBlendLevels(resolvedLevel, layerBlendMode);

  if (layerLevels.length <= 1) {
    const velocityTexture = await createVelocityTexture(resolvedLevel);
    velocityTexture.userData.layerBlendMode = layerBlendMode;
    velocityTexture.userData.layerBlendLevels = [resolvedLevel];
    return velocityTexture;
  }

  const layerTextures = await Promise.all(
    layerLevels.map((level) => createVelocityTexture(level))
  );

  return createBlendedVelocityTexture(layerTextures, {
    level: resolvedLevel,
    layerBlendMode,
    layerBlendLevels: layerLevels,
  });
}

export function disposeVelocityTexture(texture) {
  const layerTextures = texture?.userData?.layerTextures;

  if (Array.isArray(layerTextures)) {
    layerTextures.forEach((layerTexture) => {
      if (layerTexture && layerTexture !== texture) {
        layerTexture.dispose();
      }
    });
  }

  texture?.dispose();
}

export async function createPressureTexture(textureLevel = DEFAULT_VELOCITY_TEXTURE_LEVEL) {
  const resolvedLevel = resolveVelocityTextureLevel(textureLevel);
  const pressureTexture = await loadTextureAsync(getPressureTexturePath(resolvedLevel));

  pressureTexture.userData.level = resolvedLevel;
  pressureTexture.minFilter = THREE.LinearFilter;
  pressureTexture.magFilter = THREE.LinearFilter;
  pressureTexture.generateMipmaps = false;
  pressureTexture.wrapS = THREE.RepeatWrapping;
  pressureTexture.wrapT = THREE.ClampToEdgeWrapping;
  pressureTexture.colorSpace = THREE.SRGBColorSpace;
  pressureTexture.needsUpdate = true;

  return pressureTexture;
}

export async function createTempTexture() {
  const tempTexture = await loadTextureAsync(TEMPERATURE_TEXTURE_PATH);

  tempTexture.minFilter = THREE.LinearFilter;
  tempTexture.magFilter = THREE.LinearFilter;
  tempTexture.generateMipmaps = false;
  tempTexture.wrapS = THREE.RepeatWrapping;
  tempTexture.wrapT = THREE.ClampToEdgeWrapping;
  tempTexture.colorSpace = THREE.SRGBColorSpace;
  tempTexture.needsUpdate = true;

  return tempTexture;
}

function createBlendedVelocityTexture(layerTextures, {
  level,
  layerBlendMode,
  layerBlendLevels,
}) {
  const baseImage = layerTextures[0]?.image;
  const width = baseImage?.naturalWidth || baseImage?.videoWidth || baseImage?.width || 0;
  const height = baseImage?.naturalHeight || baseImage?.videoHeight || baseImage?.height || 0;

  if (!width || !height) {
    throw new Error('Unable to blend wind layers because the base velocity texture is empty.');
  }

  const layerImageData = layerTextures.map((texture) => readTextureImageData(texture, width, height));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    throw new Error('Unable to create a wind layer blend canvas.');
  }

  const output = context.createImageData(width, height);
  let minMagnitude = Infinity;
  let maxMagnitude = 0;

  for (let offset = 0; offset < output.data.length; offset += 4) {
    let lonTotal = 0;
    let latTotal = 0;
    let sampleCount = 0;

    layerImageData.forEach((imageData) => {
      const { lon, lat, magnitude } = decodeWindVector({
        r: imageData.data[offset] / 255,
        g: imageData.data[offset + 1] / 255,
        b: imageData.data[offset + 2] / 255,
        a: imageData.data[offset + 3] / 255,
      });

      lonTotal += lon;
      latTotal += lat;
      sampleCount += 1;

      if (magnitude < minMagnitude) {
        minMagnitude = magnitude;
      }

      if (magnitude > maxMagnitude) {
        maxMagnitude = magnitude;
      }
    });

    const lon = lonTotal / Math.max(sampleCount, 1);
    const lat = latTotal / Math.max(sampleCount, 1);

    output.data[offset] = Math.round(encodeWindComponent(
      lon,
      WIND_LIMITS.minLon,
      WIND_LIMITS.maxLon
    ) * 255);
    output.data[offset + 1] = Math.round(encodeWindComponent(
      lat,
      WIND_LIMITS.minLat,
      WIND_LIMITS.maxLat
    ) * 255);
    output.data[offset + 2] = 0;
    output.data[offset + 3] = 255;
  }

  context.putImageData(output, 0, 0);

  const blendedTexture = new THREE.CanvasTexture(canvas);
  blendedTexture.userData.level = level;
  blendedTexture.userData.layerBlendMode = layerBlendMode;
  blendedTexture.userData.layerBlendLevels = layerBlendLevels;
  blendedTexture.userData.layerTextures = layerTextures;
  blendedTexture.userData.windMagnitudeRange = normaliseWindMagnitudeRange({
    min: Number.isFinite(minMagnitude) ? minMagnitude : 0,
    max: maxMagnitude,
  });
  blendedTexture.userData.isLayerBlend = true;
  blendedTexture.minFilter = THREE.NearestFilter;
  blendedTexture.magFilter = THREE.NearestFilter;
  blendedTexture.generateMipmaps = false;
  blendedTexture.wrapS = THREE.RepeatWrapping;
  blendedTexture.wrapT = THREE.ClampToEdgeWrapping;
  blendedTexture.colorSpace = THREE.NoColorSpace;
  blendedTexture.needsUpdate = true;

  return blendedTexture;
}

function readTextureImageData(texture, width, height) {
  const image = texture?.image;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!image || !context) {
    throw new Error('Unable to read a wind layer image for blending.');
  }

  context.drawImage(image, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

function encodeWindComponent(value, min, max) {
  return clamp((value - min) / Math.max(max - min, 1e-6), 0, 1);
}

export async function createPlanetTexture() {
  const surfaceTexture = await loadTextureAsync(withBasePath('/textures/worldMap.png'));

  surfaceTexture.minFilter = THREE.LinearFilter;
  surfaceTexture.magFilter = THREE.LinearFilter;
  surfaceTexture.wrapS = THREE.RepeatWrapping;
  surfaceTexture.wrapT = THREE.ClampToEdgeWrapping;
  surfaceTexture.colorSpace = THREE.SRGBColorSpace;
  surfaceTexture.needsUpdate = true;

  return surfaceTexture;
}

export function measureWindMagnitudeRange(velocityTexture) {
  const image = velocityTexture?.image;

  if (!image) {
    return WIND_MAGNITUDE_RANGE;
  }

  if (velocityTexture.userData?.windMagnitudeRange) {
    return velocityTexture.userData.windMagnitudeRange;
  }

  if (windMagnitudeRangeCache.has(image)) {
    return windMagnitudeRangeCache.get(image);
  }

  const width = image.naturalWidth || image.videoWidth || image.width || 0;
  const height = image.naturalHeight || image.videoHeight || image.height || 0;

  if (!width || !height) {
    return WIND_MAGNITUDE_RANGE;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return WIND_MAGNITUDE_RANGE;
  }

  context.drawImage(image, 0, 0, width, height);

  let data;

  try {
    data = context.getImageData(0, 0, width, height).data;
  } catch (error) {
    return WIND_MAGNITUDE_RANGE;
  }

  let minMagnitude = Infinity;
  let maxMagnitude = 0;

  for (let offset = 0; offset < data.length; offset += 4) {
    const { magnitude } = decodeWindVector({
      r: data[offset] / 255,
      g: data[offset + 1] / 255,
      b: data[offset + 2] / 255,
      a: data[offset + 3] / 255,
    });

    if (magnitude < minMagnitude) {
      minMagnitude = magnitude;
    }

    if (magnitude > maxMagnitude) {
      maxMagnitude = magnitude;
    }
  }

  const range = normaliseWindMagnitudeRange({
    min: Number.isFinite(minMagnitude) ? minMagnitude : 0,
    max: maxMagnitude,
  });

  windMagnitudeRangeCache.set(image, range);
  velocityTexture.userData.windMagnitudeRange = range;

  return range;
}
